import { deriveSkillEditorReferences } from "./skill-editor-references";
import {
    getSkillEditorPublishedFilename,
    skillEditorFileNamesCollide,
} from "./skill-editor-filenames";
import {
    isSkillEditorNameNormalizationRepair,
    normalizeSkillEditorText,
    SKILL_INDEX_CLIENT_KEY,
    skillEditorFieldsFromSource,
    skillEditorScopesEqual,
    type SkillEditorFile,
    type SkillEditorSource,
    type SkillEditorSourceFile,
    type SkillEditorState,
} from "./skill-editor-state";
import {
    captureSkillEditorSource,
    captureSkillEditorState,
    classifySkillEditorConflicts,
    createSkillEditorConflictFence,
    fileFieldValuesEqual,
    PREFLIGHT_FILE_FIELDS,
    PREFLIGHT_NON_SCOPE_SKILL_FIELDS,
    recordSkillEditorSourceFile,
    skillEditorFileFieldChanged,
    skillFieldValuesEqual,
} from "./skill-editor-save-conflicts";
import {
    planSkillEditorSave,
    validateSkillEditorState,
    type SkillEditorConflictCase,
    type SkillEditorSaveConflict,
    type SkillEditorSaveIntent,
    type SkillEditorSaveOperation,
    type SkillEditorSavePreflight,
    type SkillEditorSaveReconcileResult,
} from "./skill-editor-save";

const selectedEditorContent = (state: SkillEditorState) => {
    if (state.selectedClientKey === SKILL_INDEX_CLIENT_KEY) {
        return state.buffer.skill.content;
    }
    return state.buffer.files.find(
        (file) => file.clientKey === state.selectedClientKey
    )?.content;
};

const withSelectedEditorResetEpoch = (
    state: SkillEditorState,
    candidate: SkillEditorState
): SkillEditorState => {
    const resetEpoch =
        candidate.selectedClientKey === state.selectedClientKey &&
        selectedEditorContent(candidate) !== selectedEditorContent(state)
            ? state.resetEpoch + 1
            : state.resetEpoch;
    return { ...candidate, resetEpoch };
};

export function reloadSkillEditorRecoveryFile(
    state: SkillEditorState,
    clientKey: string,
    source: SkillEditorSourceFile
): SkillEditorState {
    if (!state.buffer.files.some((file) => file.clientKey === clientKey)) {
        return state;
    }
    const withoutDuplicate: SkillEditorState = {
        ...state,
        baseline: {
            ...state.baseline,
            files: state.baseline.files.filter(
                (file) =>
                    file.serverId !== source.id || file.clientKey === clientKey
            ),
        },
        buffer: {
            ...state.buffer,
            files: state.buffer.files.filter(
                (file) =>
                    file.serverId !== source.id || file.clientKey === clientKey
            ),
        },
    };
    const recorded = recordSkillEditorSourceFile(
        withoutDuplicate,
        clientKey,
        source
    );
    const candidate: SkillEditorState = {
        ...recorded,
        buffer: {
            ...recorded.buffer,
            files: recorded.buffer.files.map((file) =>
                file.clientKey === clientKey
                    ? {
                          ...file,
                          name: source.name,
                          content: source.content,
                          renameOrigin: null,
                          acknowledgedRename: null,
                      }
                    : file
            ),
        },
        renameRequest:
            recorded.renameRequest?.clientKey === clientKey
                ? null
                : recorded.renameRequest,
    };
    return withSelectedEditorResetEpoch(state, candidate);
}

const rebaseSkillEditorState = (
    state: SkillEditorState,
    authoritative: SkillEditorSource
): SkillEditorState => {
    const remoteSkill = skillEditorFieldsFromSource(authoritative);
    const skill = { ...state.buffer.skill };
    if (
        skillEditorScopesEqual(state.buffer.skill, state.baseline.skill) ||
        skillEditorScopesEqual(state.buffer.skill, remoteSkill)
    ) {
        Object.assign(skill, {
            scopeKind: remoteSkill.scopeKind,
            organizationId: remoteSkill.organizationId,
            organizationGroupId: remoteSkill.organizationGroupId,
        });
    }
    for (const field of PREFLIGHT_NON_SCOPE_SKILL_FIELDS) {
        const baselineValue = state.baseline.skill[field];
        const localValue = state.buffer.skill[field];
        const remoteValue = remoteSkill[field];
        const localChanged = !skillFieldValuesEqual(
            field,
            localValue,
            baselineValue
        );
        if (
            !localChanged ||
            skillFieldValuesEqual(field, localValue, remoteValue)
        ) {
            Object.assign(skill, { [field]: remoteValue });
        }
    }

    const baselineByServerId = new Map(
        state.baseline.files.map((file) => [file.serverId, file])
    );
    const remoteByServerId = new Map(
        authoritative.skill_files.map((file) => [file.id, file])
    );
    const representedServerIds = new Set<number>();
    const files: SkillEditorFile[] = [];
    let nextCreatedOrder = Math.max(
        -1,
        ...state.buffer.files.map((file) => file.createdOrder)
    );

    for (const local of state.buffer.files) {
        if (local.serverId === null) {
            files.push(local);
            continue;
        }
        representedServerIds.add(local.serverId);
        const baseline = baselineByServerId.get(local.serverId);
        const remote = remoteByServerId.get(local.serverId);
        if (!baseline) {
            if (remote) files.push(local);
            continue;
        }
        if (!remote) {
            const locallyChanged = PREFLIGHT_FILE_FIELDS.some((field) =>
                skillEditorFileFieldChanged(local, baseline, field)
            );
            if (locallyChanged) {
                files.push({
                    ...local,
                    serverId: null,
                    isPlaceholder: false,
                    renameOrigin: null,
                    acknowledgedRename: null,
                });
            }
            continue;
        }

        const next = { ...local };
        for (const field of PREFLIGHT_FILE_FIELDS) {
            const localChanged = skillEditorFileFieldChanged(
                local,
                baseline,
                field
            );
            const normalizationRepair =
                field === "name" &&
                isSkillEditorNameNormalizationRepair(local.name, baseline.name);
            if (
                !localChanged ||
                (fileFieldValuesEqual(field, local[field], remote[field]) &&
                    !normalizationRepair)
            ) {
                Object.assign(next, { [field]: remote[field] });
            }
        }
        const nameChanged = !fileFieldValuesEqual(
            "name",
            next.name,
            remote.name
        );
        files.push({
            ...next,
            renameOrigin: nameChanged
                ? (local.renameOrigin ?? remote.name)
                : null,
            acknowledgedRename:
                nameChanged && local.acknowledgedRename === next.name
                    ? local.acknowledgedRename
                    : null,
        });
    }

    for (const remote of authoritative.skill_files) {
        if (representedServerIds.has(remote.id)) continue;
        if (baselineByServerId.has(remote.id)) continue;
        nextCreatedOrder += 1;
        files.push({
            clientKey: `file:${remote.id}`,
            serverId: remote.id,
            name: remote.name,
            content: remote.content,
            createdOrder: nextCreatedOrder,
            isPlaceholder: false,
            renameOrigin: null,
            acknowledgedRename: null,
        });
    }

    const remoteServerIds = new Set(
        authoritative.skill_files.map((file) => file.id)
    );
    const baselineFiles = authoritative.skill_files.map((file) => {
        const previous = baselineByServerId.get(file.id);
        return {
            clientKey: previous?.clientKey ?? `file:${file.id}`,
            serverId: file.id,
            name: file.name,
            content: file.content,
            updatedAt: file.updated_at,
            displayName: file.name,
        };
    });
    const selectedClientKey =
        state.selectedClientKey === SKILL_INDEX_CLIENT_KEY ||
        files.some((file) => file.clientKey === state.selectedClientKey)
            ? state.selectedClientKey
            : SKILL_INDEX_CLIENT_KEY;
    const renameTarget = state.renameRequest
        ? files.find(
              (file) => file.clientKey === state.renameRequest?.clientKey
          )
        : undefined;
    const candidate: SkillEditorState = {
        ...state,
        baseline: {
            skill: {
                skillId: authoritative.id,
                ...remoteSkill,
                updatedAt: authoritative.updated_at,
                claudeSkillId: authoritative.claude_skill_id ?? null,
                publishedAt: authoritative.published_at ?? null,
            },
            files: baselineFiles,
            seededServerIds: state.baseline.seededServerIds.filter((serverId) =>
                remoteServerIds.has(serverId)
            ),
        },
        buffer: { skill, files },
        selectedClientKey,
        renameRequest:
            renameTarget?.serverId !== null &&
            renameTarget?.name === state.renameRequest?.destination
                ? state.renameRequest
                : null,
    };
    return withSelectedEditorResetEpoch(state, candidate);
};

export function markSkillEditorVisibleFilesSeeded(
    state: SkillEditorState,
    authoritative: SkillEditorSource
): SkillEditorState {
    const remoteServerIds = new Set(
        authoritative.skill_files.map((file) => file.id)
    );
    return {
        ...state,
        baseline: {
            ...state.baseline,
            seededServerIds: state.baseline.files
                .map((file) => file.serverId)
                .filter((serverId) => remoteServerIds.has(serverId)),
        },
    };
}

interface SkillEditorReconcileCollision {
    readonly local: SkillEditorFile;
    readonly remote: SkillEditorSourceFile;
    readonly publishedPath: string;
}

const findSkillEditorReconcileCollision = (
    state: SkillEditorState,
    authoritative: SkillEditorSource,
    clientKey: string | null = null
): SkillEditorReconcileCollision | null => {
    const baselineByClientKey = new Map(
        state.baseline.files.map((file) => [file.clientKey, file])
    );
    const localCandidates = state.buffer.files.filter((file) => {
        if (clientKey !== null && file.clientKey !== clientKey) return false;
        if (file.isPlaceholder) return false;
        if (file.serverId === null) return true;
        const baseline = baselineByClientKey.get(file.clientKey);
        return (
            baseline !== undefined &&
            skillEditorFileFieldChanged(file, baseline, "name")
        );
    });

    for (const local of localCandidates) {
        const localName = normalizeSkillEditorText(local.name);
        if (localName.length === 0) continue;
        const publishedPath = getSkillEditorPublishedFilename(localName);
        for (const remote of authoritative.skill_files) {
            if (local.serverId === remote.id) continue;
            const remoteName = normalizeSkillEditorText(remote.name);
            if (remoteName.length === 0) continue;
            if (skillEditorFileNamesCollide(localName, remoteName)) {
                return { local, remote, publishedPath };
            }
        }
    }
    return null;
};

export function reconcileSkillEditorSaveFailure(
    state: SkillEditorState,
    authoritative: SkillEditorSource,
    failedOperation: SkillEditorSaveOperation
): SkillEditorSaveReconcileResult {
    const failedBaseline =
        failedOperation.kind === "update-file"
            ? state.baseline.files.find(
                  (file) =>
                      file.clientKey === failedOperation.clientKey &&
                      file.serverId === failedOperation.fileId
              )
            : undefined;
    const reconciled = rebaseSkillEditorState(state, authoritative);
    const visibleState = markSkillEditorVisibleFilesSeeded(
        reconciled,
        authoritative
    );
    const failedSurvivor =
        failedOperation.kind === "update-file" &&
        failedBaseline !== undefined &&
        !authoritative.skill_files.some(
            (file) => file.id === failedOperation.fileId
        )
            ? reconciled.buffer.files.find(
                  (file) =>
                      file.clientKey === failedOperation.clientKey &&
                      file.serverId === null
              )
            : undefined;
    const failedClientKey =
        failedOperation.kind === "update-file" ||
        failedOperation.kind === "add-file"
            ? failedOperation.clientKey
            : null;
    const failedCollision =
        failedClientKey === null
            ? null
            : findSkillEditorReconcileCollision(
                  reconciled,
                  authoritative,
                  failedClientKey
              );
    const collision =
        failedCollision ??
        (failedSurvivor === undefined
            ? findSkillEditorReconcileCollision(reconciled, authoritative)
            : null);

    if (collision?.local.serverId === null) {
        return {
            state: visibleState,
            recovery: {
                kind: "idless-local-server-collision",
                clientKey: collision.local.clientKey,
                localFileName: collision.local.name,
                remoteServerId: collision.remote.id,
                remoteFileName: collision.remote.name,
                remoteUpdatedAt: collision.remote.updated_at,
                publishedPath: collision.publishedPath,
            },
        };
    }

    if (collision && collision.local.serverId !== null) {
        return {
            state: visibleState,
            recovery: {
                kind: "server-file-collision",
                clientKey: collision.local.clientKey,
                localServerId: collision.local.serverId,
                localFileName: collision.local.name,
                remoteServerId: collision.remote.id,
                remoteFileName: collision.remote.name,
                remoteUpdatedAt: collision.remote.updated_at,
                publishedPath: collision.publishedPath,
            },
        };
    }

    if (failedSurvivor && failedBaseline !== undefined) {
        return {
            state: visibleState,
            recovery: {
                kind: "idless-local",
                clientKey: failedSurvivor.clientKey,
                fileName: failedSurvivor.name,
            },
        };
    }

    return { state: visibleState, recovery: null };
}

const overwriteSkillEditorCollisionFiles = (
    state: SkillEditorState,
    capturedState: SkillEditorState,
    cases: readonly SkillEditorConflictCase[]
): SkillEditorState => {
    const overwrittenServerIds = new Set(
        cases.flatMap((conflictCase) =>
            conflictCase.kind === "concurrent-filename-or-path-collision"
                ? conflictCase.collisions.map(
                      ({ remoteServerId }) => remoteServerId
                  )
                : []
        )
    );
    if (overwrittenServerIds.size === 0) return state;

    const capturedBaselineByServerId = new Map(
        capturedState.baseline.files.map((file) => [file.serverId, file])
    );
    const preservedFiles: SkillEditorFile[] = [];
    const preservedClientKeys = new Map<string, string>();
    let nextLocalFileKey = state.nextLocalFileKey;
    for (const file of capturedState.buffer.files) {
        if (
            file.serverId === null ||
            !overwrittenServerIds.has(file.serverId)
        ) {
            continue;
        }
        const baseline = capturedBaselineByServerId.get(file.serverId);
        if (
            !baseline ||
            !PREFLIGHT_FILE_FIELDS.some((field) =>
                skillEditorFileFieldChanged(file, baseline, field)
            )
        ) {
            continue;
        }
        const clientKey = `new:${nextLocalFileKey}`;
        nextLocalFileKey += 1;
        preservedClientKeys.set(file.clientKey, clientKey);
        preservedFiles.push({
            ...file,
            clientKey,
            serverId: null,
            isPlaceholder: false,
            renameOrigin: null,
            acknowledgedRename: null,
        });
    }
    const files = [
        ...state.buffer.files.filter(
            ({ serverId }) =>
                serverId === null || !overwrittenServerIds.has(serverId)
        ),
        ...preservedFiles,
    ];
    const selectedClientKey =
        preservedClientKeys.get(state.selectedClientKey) ??
        (files.some(({ clientKey }) => clientKey === state.selectedClientKey)
            ? state.selectedClientKey
            : SKILL_INDEX_CLIENT_KEY);
    const candidate: SkillEditorState = {
        ...state,
        baseline: {
            ...state.baseline,
            seededServerIds: state.baseline.files
                .map(({ serverId }) => serverId)
                .filter(
                    (serverId) =>
                        state.baseline.seededServerIds.includes(serverId) ||
                        overwrittenServerIds.has(serverId)
                ),
        },
        buffer: { ...state.buffer, files },
        selectedClientKey,
        nextLocalFileKey,
        renameRequest:
            state.renameRequest &&
            files.some(
                ({ clientKey }) => clientKey === state.renameRequest?.clientKey
            )
                ? state.renameRequest
                : null,
    };
    return withSelectedEditorResetEpoch(state, candidate);
};

export function preflightSkillEditorSave(
    state: SkillEditorState,
    authoritative: SkillEditorSource,
    intent: SkillEditorSaveIntent,
    acknowledgedRemoteToken: string | null = null
): SkillEditorSavePreflight {
    const fence = createSkillEditorConflictFence(authoritative);
    const cases = classifySkillEditorConflicts(state, authoritative);
    if (cases.length > 0 && acknowledgedRemoteToken !== fence.token) {
        return {
            kind: "conflict",
            conflict: {
                fence,
                cases,
                authoritative: captureSkillEditorSource(authoritative),
                capturedState: captureSkillEditorState(state),
            },
        };
    }

    const rebasedState = rebaseSkillEditorState(state, authoritative);
    const overwrittenState =
        cases.length > 0 && acknowledgedRemoteToken === fence.token
            ? overwriteSkillEditorCollisionFiles(rebasedState, state, cases)
            : rebasedState;
    const references = deriveSkillEditorReferences(overwrittenState);
    const validation = validateSkillEditorState(overwrittenState, references);
    if (!validation.isValid) {
        return {
            kind: "invalid",
            state: overwrittenState,
            validation,
            fence,
        };
    }
    return {
        kind: "ready",
        state: overwrittenState,
        plan: planSkillEditorSave(overwrittenState, intent, references),
        fence,
    };
}

export function reloadSkillEditorConflicts(
    state: SkillEditorState,
    conflict: SkillEditorSaveConflict
): SkillEditorState {
    const authoritativeByServerId = new Map(
        conflict.authoritative.skill_files.map((file) => [file.id, file])
    );
    const authoritativeSkill = skillEditorFieldsFromSource(
        conflict.authoritative
    );
    let skill = { ...state.buffer.skill };
    let files = state.buffer.files.map((file) => ({ ...file }));
    let reloadScope = false;

    for (const conflictCase of conflict.cases) {
        if (conflictCase.kind === "overlapping-edits") {
            for (const fieldConflict of conflictCase.fields) {
                if (fieldConflict.scope === "skill") {
                    if (
                        fieldConflict.field === "scopeKind" ||
                        fieldConflict.field === "organizationId" ||
                        fieldConflict.field === "organizationGroupId"
                    ) {
                        reloadScope = true;
                        continue;
                    }
                    skill = {
                        ...skill,
                        [fieldConflict.field]: fieldConflict.remoteValue,
                    };
                    continue;
                }
                files = files.map((file) =>
                    file.clientKey === fieldConflict.clientKey
                        ? {
                              ...file,
                              [fieldConflict.field]: fieldConflict.remoteValue,
                              ...(fieldConflict.field === "name"
                                  ? {
                                        renameOrigin: null,
                                        acknowledgedRename: null,
                                    }
                                  : {}),
                          }
                        : file
                );
            }
            continue;
        }

        if (conflictCase.kind === "remote-edit-against-local-delete") {
            for (const deleted of conflictCase.files) {
                if (
                    files.some((file) => file.clientKey === deleted.clientKey)
                ) {
                    continue;
                }
                const remote = authoritativeByServerId.get(deleted.serverId);
                if (!remote) continue;
                const baselineIndex =
                    conflict.capturedState.baseline.files.findIndex(
                        (file) => file.clientKey === deleted.clientKey
                    );
                files.splice(Math.max(0, baselineIndex), 0, {
                    clientKey: deleted.clientKey,
                    serverId: deleted.serverId,
                    name: remote.name,
                    content: remote.content,
                    createdOrder: Math.max(0, baselineIndex),
                    isPlaceholder: false,
                    renameOrigin: null,
                    acknowledgedRename: null,
                });
            }
            continue;
        }

        if (conflictCase.kind === "local-edit-against-remote-delete") {
            const localKeys = new Set(
                conflictCase.files.map(({ clientKey }) => clientKey)
            );
            files = files.filter(({ clientKey }) => !localKeys.has(clientKey));
            continue;
        }

        const localKeys = new Set(
            conflictCase.collisions.map(({ localClientKey }) => localClientKey)
        );
        files = files.flatMap((file) => {
            if (!localKeys.has(file.clientKey)) return [file];
            if (file.serverId === null) return [file];
            const remote = authoritativeByServerId.get(file.serverId);
            if (!remote) return [file];
            return [
                {
                    ...file,
                    name: remote.name,
                    renameOrigin: null,
                    acknowledgedRename: null,
                },
            ];
        });
    }

    if (reloadScope) {
        skill = {
            ...skill,
            scopeKind: authoritativeSkill.scopeKind,
            organizationId: authoritativeSkill.organizationId,
            organizationGroupId: authoritativeSkill.organizationGroupId,
        };
    }

    const selectedClientKey = files.some(
        (file) => file.clientKey === state.selectedClientKey
    )
        ? state.selectedClientKey
        : SKILL_INDEX_CLIENT_KEY;
    const resolved: SkillEditorState = {
        ...state,
        buffer: { skill, files },
        selectedClientKey,
        editGeneration: state.editGeneration + 1,
        renameRequest:
            state.renameRequest &&
            files.some(
                (file) => file.clientKey === state.renameRequest?.clientKey
            )
                ? state.renameRequest
                : null,
    };
    const rebased = withSelectedEditorResetEpoch(
        state,
        rebaseSkillEditorState(resolved, conflict.authoritative)
    );
    return markSkillEditorVisibleFilesSeeded(rebased, conflict.authoritative);
}
